import React from "react";
import { motion } from "framer-motion";
import { ArrowRight, Building2, Users, Search, Settings, Truck, Heart, MessageSquare, FileText, Calculator, GraduationCap, Hospital } from "lucide-react";
import { Link } from "react-router-dom";
import service11 from "../../Assests/servicehome11.jpg";
import service5 from "../../Assests/servicehome5.jpg";
import service7 from "../../Assests/servicehome7.jpg";
import service6 from "../../Assests/servicehome6.jpg";
import service9 from "../../Assests/servicehome9.jpg";

const ServicesSection = () => {

  const services = [
    {
      id: 1,
      title: "NGO Consultancy",
      description: "One-stop solution for NGOs across India including business development, proposal writing, documentation, and digital support for institutional strengthening.",
      image: service11,
      icon: Building2,
      link: "/ngo-consultancy"
    },
    {
      id: 2,
      title: "HR Solutions",
      description: "Quality human resource solutions with proven track record of providing stable, qualified staff through rigorous due diligence processes.",
      image: service5,
      icon: Users,
      link: "/hr-solutions"
    },
    {
      id: 3,
      title: "Research & Studies",
      description: "Expert research support including questionnaire design, data collection (qualitative & quantitative), analysis, and quality report preparation.",
      image: service7,
      icon: Search,
      link: "/research-studies"
    },
    {
      id: 4,
      title: "Technical Assistance",
      description: "Comprehensive technical support for planning, execution, MIS development, and monitoring of national programs and schemes.",
      image: service6,
      icon: Settings,
      link: "/technical-assistance"
    },
    {
      id: 5,
      title: "Supply Chain Management",
      description: "Streamlined and cost-effective supply chain solutions that maximize customer value and provide competitive marketplace advantages.",
      image: service9,
      icon: Truck,
      link: "/supply-chain"
    },
    {
      id: 6,
      title: "Health and Hospital",
      description: "Expert-led hospital assessments (NABH, ISO, NQAS, LaQshya, Kayakalp) with strategic accreditation support and adaptive healthcare solutions.",
      image: service11,
      icon: Hospital,
      link: "/health-and-hospital"
    },
    {
      id: 7,
      title: "CSR Solutions",
      description: "Expertise in project formulation, implementation, and assessment to support corporate social responsibility initiatives across sectors.",
      image: service5,
      icon: Heart,
      link: "/csr-solutions"
    },
    {
      id: 8,
      title: "SBCC Services",
      description: "Social and Behaviour Change Communication strategies, planning, and development of innovative, cost-effective IEC materials.",
      image: service7,
      icon: MessageSquare,
      link: "/sbcc-services"
    },
    {
      id: 9,
      title: "Documentation & Translation",
      description: "Comprehensive documentation support including annual reports, case studies, process documentation, translation, and proofreading services.",
      image: service6,
      icon: FileText,
      link: "/documentation"
    },
    {
      id: 10,
      title: "Financial Compliance",
      description: "Complete financial and statutory compliance solutions including audit reports, registrations, certifications, and regulatory support.",
      image: service9,
      icon: Calculator,
      link: "/financial-compliance"
    },
    {
      id: 11,
      title: "Education & Training",
      description: "Training design, module development, community education programs, and skill development initiatives for employment and behavior change.",
      image: service11,
      icon: GraduationCap,
      link: "/education-training"
    }
  ];

  return (
    <section className="py-16 sm:py-20 bg-gradient-to-br from-gray-50 to-white relative overflow-hidden">
      {/* Subtle background pattern */}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_80%,rgba(37,99,235,0.03),transparent_50%)]"></div>
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_80%_20%,rgba(8,145,178,0.02),transparent_50%)]"></div>
      <div className="absolute top-0 left-0 w-full h-full bg-[linear-gradient(45deg,transparent_25%,rgba(37,99,235,0.01)_50%,transparent_75%)] bg-[length:20px_20px]"></div>
      
      <div className="container-custom px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-12 sm:mb-16"
        >
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4 sm:mb-6">
            Our <span className="text-transparent bg-clip-text bg-gradient-to-r from-lumbini-blue to-lumbini-cyan">Services</span>
          </h2>
          <p className="text-lg sm:text-xl text-gray-600 max-w-3xl mx-auto px-4">
            Comprehensive consulting solutions tailored to meet your unique business needs and drive sustainable growth.
          </p>
        </motion.div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 sm:gap-8 items-stretch">
          {services.map((service, index) => (
            <div
              key={service.id}
              className="group"
            >
              <div className="bg-white rounded-2xl h-full flex flex-col overflow-hidden transition-all duration-500 border border-gray-200 hover:border-primary-300 hover:shadow-xl hover:-translate-y-2">
                {/* Image */}
                <div className="relative h-48 sm:h-56 lg:h-64 overflow-hidden">
                  <img
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                    src={service.image}
                    alt={service.title}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-lumbini-blue/70 to-transparent"></div>
                  <div className="absolute top-4 right-4 sm:top-5 sm:right-5 w-10 h-10 sm:w-12 sm:h-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center">
                    <service.icon className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
                  </div>
                  <div className="absolute bottom-4 left-4 sm:bottom-5 sm:left-5 text-white">
                    <h3 className="text-lg sm:text-xl lg:text-2xl font-bold">{service.title}</h3>
                  </div>
                </div>

                {/* Content */}
                <div className="p-4 sm:p-6 flex flex-col h-full">
                  <p className="text-left text-sm sm:text-base text-gray-600 leading-relaxed flex-grow">
                    {service.description}
                  </p>

                  <div className="flex items-center justify-start">
                    <Link 
                      to={service.link}
                      className="inline-block"
                    >
                      <button 
                        className="text-lumbini-blue font-semibold hover:text-lumbini-cyan transition-all duration-300 flex items-center group px-2 py-1 rounded-md hover:bg-lumbini-blue/5 relative z-10"
                        onClick={() => console.log('Navigating to:', service.link)}
                      >
                        <span>Learn More</span>
                        <ArrowRight className="ml-1 h-4 w-4 group-hover:translate-x-1 transition-transform" />
                      </button>
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ServicesSection;

